import React, { useState, useEffect, useMemo } from 'react';
import { maskPhone, validatePhone, limitLength } from '../../services/validationService';
import { api } from '../../services/api';
import { useToast } from '../../components/UI/ToastProvider';
import { ConfirmModal } from '../../components/UI/ConfirmModal';
import { Customer } from '../../types/customer';
import { useNavigate } from 'react-router-dom';
import { Button } from '../../components/UI/Button';
import {
  TenantAccessMode,
  buildBarukFqdn,
  deriveEnterpriseCustomSlugBaseFromDomain,
  normalizeSlugInput,
  validateCustomDomain,
  validateTenantSlugDnsLabel,
} from '../../services/customerTenantValidation';
import { formatCpfCnpjInput, validateCpfCnpj } from '../../utils/cpfCnpjUtils';

const initialCustomers: Customer[] = [];

type NewCustomerForm = {
  name: string;
  slug: string;
  cnpjCpf: string;
  email: string;
  phone: string;
  domain: string;
  tenantAccessMode: TenantAccessMode;
};

const emptyForm = (): NewCustomerForm => ({
  name: '',
  slug: '',
  cnpjCpf: '',
  email: '',
  phone: '',
  domain: '',
  tenantAccessMode: TenantAccessMode.EnterpriseCustom,
});

function maskCpfCnpj(value: string) {
  return formatCpfCnpjInput(value);
}

export const CustomersPage: React.FC = () => {
  const [customers, setCustomers] = useState<Customer[]>(initialCustomers);
  const [form, setForm] = useState<NewCustomerForm>(emptyForm());
  const [showForm, setShowForm] = useState(false);
  const [errors, setErrors] = useState<{ [key: string]: string }>({});
  const { addToast } = useToast();
  const [confirmDelete, setConfirmDelete] = useState<{ open: boolean; customer: Customer | null }>({ open: false, customer: null });
  const navigate = useNavigate();

  const isBarukSubdomain =
    form.tenantAccessMode === TenantAccessMode.Fit ||
    form.tenantAccessMode === TenantAccessMode.EnterpriseBasic;

  const previewHost = useMemo(() => {
    if (!isBarukSubdomain) return '';
    const n = normalizeSlugInput(form.slug);
    return buildBarukFqdn(form.tenantAccessMode, n);
  }, [form.slug, form.tenantAccessMode, isBarukSubdomain]);

  const derivedEnterpriseSlugBase = useMemo(
    () => deriveEnterpriseCustomSlugBaseFromDomain(form.domain),
    [form.domain]
  );

  useEffect(() => {
    async function fetchCustomers() {
      try {
        const response = await api.get('/customers');
        setCustomers(response.data);
      } catch (error: unknown) {
        let msg = 'Erro ao carregar clientes.';
        if (error && typeof error === 'object' && 'response' in error) {
          const ax = error as { response?: { data?: { message?: string; title?: string } } };
          const d = ax.response?.data;
          if (d?.message) msg = d.message;
          else if (d?.title) msg = d.title;
        }
        addToast(msg, 'error');
      }
    }
    fetchCustomers();
  }, [addToast]);

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
    const { name, value } = e.target;
    let newValue: string | number = value;
    if (name === 'phone') {
      newValue = maskPhone(value);
      newValue = limitLength(newValue, 15);
    } else if (name === 'cnpjCpf') {
      newValue = maskCpfCnpj(value);
      newValue = limitLength(newValue, 18);
    } else if (name === 'name') {
      newValue = limitLength(value, 60);
    } else if (name === 'email') {
      newValue = limitLength(value, 60);
    } else if (name === 'domain') {
      newValue = limitLength(value, 255);
    } else if (name === 'slug') {
      newValue = limitLength(value, 100);
    }
    setForm(prev => ({ ...prev, [name]: newValue }));
  };

  const handleNew = () => {
    setForm(emptyForm());
    setShowForm(true);
    setErrors({});
  };

  const handleEdit = (customer: Customer) => {
    navigate(`/admin/customers/${customer.id}/edit`);
  };

  const handleDelete = (customer: Customer) => {
    setConfirmDelete({ open: true, customer });
  };

  const confirmDeleteCustomer = async () => {
    if (!confirmDelete.customer) return;
    try {
      await api.delete(`/customers/${confirmDelete.customer.id}`);
      setCustomers(customers.filter(c => c.id !== confirmDelete.customer!.id));
      addToast('Cliente excluído com sucesso.', 'success');
    } catch (error: unknown) {
      let msg = 'Erro ao excluir cliente.';
      if (error && typeof error === 'object' && 'response' in error) {
        const ax = error as { response?: { data?: { message?: string; title?: string } } };
        const d = ax.response?.data;
        if (d?.message) msg = d.message;
        else if (d?.title) msg = d.title;
      }
      addToast(msg, 'error');
    } finally {
      setConfirmDelete({ open: false, customer: null });
    }
  };

  const validateForm = () => {
    const newErrors: { [key: string]: string } = {};
    if (!form.name.trim()) newErrors.name = 'Nome é obrigatório';
    if (!validateCpfCnpj(form.cnpjCpf || '')) newErrors.cnpjCpf = 'CNPJ/CPF inválido';
    if (!validatePhone(form.phone || '')) newErrors.phone = 'Telefone inválido';
    if (!(form.email || '').trim()) newErrors.email = 'E-mail é obrigatório';

    if (isBarukSubdomain) {
      const slugErr = validateTenantSlugDnsLabel(normalizeSlugInput(form.slug));
      if (slugErr) newErrors.slug = slugErr;
    } else {
      const domainErr = validateCustomDomain(form.domain);
      if (domainErr) newErrors.domain = domainErr;
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!validateForm()) return;
    try {
      const payload: Record<string, unknown> = {
        name: form.name,
        email: form.email,
        phone: form.phone,
        cnpjCpf: form.cnpjCpf,
        tenantAccessMode: form.tenantAccessMode,
        active: true,
      };
      if (isBarukSubdomain) {
        payload.slug = normalizeSlugInput(form.slug);
      } else {
        payload.domain = form.domain.trim().toLowerCase();
      }

      const response = await api.post('/customers', payload);
      setCustomers([...customers, { ...response.data, isActive: true }]);
      setShowForm(false);
      setForm(emptyForm());
      setErrors({});
      addToast('Cliente cadastrado com sucesso.', 'success');
    } catch (error: unknown) {
      let msg = 'Erro ao cadastrar cliente.';
      if (error && typeof error === 'object' && 'response' in error) {
        const ax = error as { response?: { data?: { message?: string; title?: string } } };
        const d = ax.response?.data;
        if (d?.message) msg = d.message;
        else if (d?.title) msg = d.title;
      }
      addToast(msg, 'error');
    }
  };

  return (
    <div className="p-8">
      <h1 className="text-2xl font-bold mb-4">Clientes</h1>
      <button
        className="mb-4 px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700"
        onClick={handleNew}
      >
        Novo Cliente
      </button>
      <table className="min-w-full bg-white rounded shadow">
        <thead>
          <tr>
            <th className="py-2 px-4 border-b text-left">Nome</th>
            <th className="py-2 px-4 border-b text-left">CNPJ/CPF</th>
            <th className="py-2 px-4 border-b text-left">Status</th>
            <th className="py-2 px-4 border-b text-left">Ações</th>
          </tr>
        </thead>
        <tbody>
          {customers.length === 0 && (
            <tr>
              <td colSpan={4} className="text-center py-4 text-gray-500">Nenhum cliente cadastrado.</td>
            </tr>
          )}
          {customers.map(customer => (
            <tr key={customer.id}>
              <td className="py-2 px-4 border-b">{customer.name}</td>
              <td className="py-2 px-4 border-b">{maskCpfCnpj(customer.cnpjCpf || '')}</td>
              <td className="py-2 px-4 border-b">
                <span className={`px-2 py-1 rounded text-xs font-semibold ${customer.isActive ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-700'}`}>
                  {customer.isActive ? 'Ativo' : 'Inativo'}
                </span>
              </td>
              <td className="py-2 px-4 border-b">
                <div className="flex gap-2 items-center">
                  <Button variant="secondary" size="sm" onClick={() => handleEdit(customer)} title="Editar Cliente">
                    <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                    </svg>
                  </Button>
                  <Button
                    variant="danger"
                    size="sm"
                    onClick={() => handleDelete(customer)}
                    title={customer.isOwner ? 'Não é possível excluir o administrador do sistema' : 'Excluir Cliente'}
                    disabled={customer.isOwner}
                    style={customer.isOwner ? { opacity: 0.5, cursor: 'not-allowed' } : {}}
                  >
                    <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                    </svg>
                  </Button>
                </div>
              </td>
            </tr>
          ))}
        </tbody>
      </table>

      {showForm && (
        <div className="fixed inset-0 bg-black bg-opacity-30 flex items-center justify-center z-50">
          <div className="bg-white p-8 rounded shadow-lg w-full max-w-md max-h-[90vh] overflow-y-auto">
            <h2 className="text-xl font-bold mb-4">Novo Cliente</h2>
            <form onSubmit={handleSubmit}>
              <div className="mb-4">
                <label className="block mb-1 font-medium">Modo de acesso *</label>
                <select
                  name="tenantAccessMode"
                  value={form.tenantAccessMode}
                  onChange={handleInputChange}
                  className="w-full border rounded px-3 py-2"
                  required
                >
                  <option value={TenantAccessMode.Fit}>
                    FIT
                  </option>
                  <option value={TenantAccessMode.EnterpriseBasic}>
                    Enterprise Basic
                  </option>
                  <option value={TenantAccessMode.EnterpriseCustom}>Enterprise - Custom Domain</option>
                </select>
              </div>
              {!isBarukSubdomain && (
                <div className="mb-4">
                  <label className="block mb-1 font-medium">Domínio *</label>
                  <input
                    type="text"
                    name="domain"
                    value={form.domain}
                    onChange={handleInputChange}
                    className="w-full border rounded px-3 py-2"
                    required
                    maxLength={255}
                    autoComplete="off"
                  />
                  {errors.domain && <span className="text-red-600 text-sm">{errors.domain}</span>}
                  <p className="text-gray-500 text-xs mt-1">
                    Ex.: cliente.com.br, sem https://. Slug interno (base):{' '}
                    <span className="font-mono text-gray-700">{derivedEnterpriseSlugBase}</span>
                  </p>
                </div>
              )}
              <div className="mb-4">
                <label className="block mb-1 font-medium">Nome</label>
                <input type="text" name="name" value={form.name} onChange={handleInputChange} className="w-full border rounded px-3 py-2" required maxLength={60} />
                {errors.name && <span className="text-red-600 text-sm">{errors.name}</span>}
              </div>
              {isBarukSubdomain && (
                <div className="mb-4">
                  <label className="block mb-1 font-medium">Slug *</label>
                  <input type="text" name="slug" value={form.slug} onChange={handleInputChange} className="w-full border rounded px-3 py-2" required maxLength={100} autoComplete="off" />
                  {errors.slug && <span className="text-red-600 text-sm">{errors.slug}</span>}
                  <p className="text-gray-500 text-xs mt-1">Host previsto: {previewHost || '—'}</p>
                </div>
              )}
              <div className="mb-4">
                <label className="block mb-1 font-medium">CNPJ/CPF</label>
                <input type="text" name="cnpjCpf" value={form.cnpjCpf} onChange={handleInputChange} className="w-full border rounded px-3 py-2" required maxLength={20} autoComplete="off" />
                {errors.cnpjCpf && <span className="text-red-600 text-sm">{errors.cnpjCpf}</span>}
              </div>
              <div className="mb-4">
                <label className="block mb-1 font-medium">E-mail</label>
                <input type="email" name="email" value={form.email} onChange={handleInputChange} className="w-full border rounded px-3 py-2" required maxLength={60} autoComplete="off" />
                {errors.email && <span className="text-red-600 text-sm">{errors.email}</span>}
              </div>
              <div className="mb-4">
                <label className="block mb-1 font-medium">Telefone</label>
                <input type="text" name="phone" value={form.phone} onChange={handleInputChange} className="w-full border rounded px-3 py-2" required maxLength={15} />
                {errors.phone && <span className="text-red-600 text-sm">{errors.phone}</span>}
              </div>
              <div className="flex justify-end gap-2">
                <Button type="button" variant="secondary" onClick={() => setShowForm(false)}>
                  Cancelar
                </Button>
                <Button type="submit" variant="primary">
                  Salvar
                </Button>
              </div>
            </form>
          </div>
        </div>
      )}
      <ConfirmModal
        isOpen={confirmDelete.open}
        title="Confirmar Exclusão"
        message={`Tem certeza que deseja excluir o cliente "${confirmDelete.customer?.name}"? Esta ação não poderá ser desfeita.`}
        onConfirm={confirmDeleteCustomer}
        onCancel={() => setConfirmDelete({ open: false, customer: null })}
        confirmText="Excluir"
        cancelText="Cancelar"
      />
    </div>
  );
};
